import { AlertTriangle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useDevicesStore, getOfflineStats } from "../../store/devicesStore";

const RATIO_THRESHOLD = 0.3;
const MIN_OFFLINE_FOR_BANNER = 3;
const DISMISSED_KEY = "vms.offlineBanner.dismissed";
const DISMISS_TTL_MS = 30 * 60_000;

// Banner amarillo en la barra superior cuando hay degradacion visible: mas del
// 30% de los devices accesibles figuran offline Y son al menos 3 dispositivos.
// Da al operador un aviso temprano de problemas de conectividad (VPN flapping,
// monitor de health en backoff, NVRs apagados, etc.) sin agregar polling: lee
// del state existente que `useDevices` y el WS de eventos mantienen al dia.
// El usuario puede dismissear el banner; vuelve a aparecer tras DISMISS_TTL_MS
// o cuando suba al threshold otra vez tras estar abajo.
export function OfflineBanner() {
  const devices = useDevicesStore((s) => s.devices);
  const nav = useNavigate();
  const loc = useLocation();
  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    try { return parseInt(sessionStorage.getItem(DISMISSED_KEY) || "0", 10) || 0; }
    catch { return 0; }
  });
  // Re-evalua dismiss si el componente se mounta de nuevo o pasa el TTL.
  useEffect(() => {
    if (!dismissedAt) return;
    const remaining = DISMISS_TTL_MS - (Date.now() - dismissedAt);
    if (remaining <= 0) {
      setDismissedAt(0);
      try { sessionStorage.removeItem(DISMISSED_KEY); } catch {}
      return;
    }
    const t = setTimeout(() => {
      setDismissedAt(0);
      try { sessionStorage.removeItem(DISMISSED_KEY); } catch {}
    }, remaining);
    return () => clearTimeout(t);
  }, [dismissedAt]);

  const stats = getOfflineStats(devices);
  const visible =
    !dismissedAt
    && stats.offline >= MIN_OFFLINE_FOR_BANNER
    && stats.ratio > RATIO_THRESHOLD;
  if (!visible) return null;

  const onDismiss = () => {
    const ts = Date.now();
    setDismissedAt(ts);
    try { sessionStorage.setItem(DISMISSED_KEY, String(ts)); } catch {}
  };
  const goDevices = () => {
    if (loc.pathname !== "/devices") nav("/devices");
  };
  const pct = Math.round(stats.ratio * 100);

  return (
    <div className="bg-amber-500/15 border-b border-amber-500/40 text-amber-200 px-3 py-1.5 flex items-center justify-between text-xs">
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} className="shrink-0" />
        <span>
          <strong>{stats.offline}</strong> de {stats.total} dispositivos
          ({pct}%) no responden. Posible problema de conectividad VPN o NVRs caidos.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={goDevices}
          className="px-2 py-0.5 rounded bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 transition-colors"
        >
          Ver dispositivos
        </button>
        <button
          onClick={onDismiss}
          className="p-1 hover:bg-amber-500/30 rounded transition-colors"
          title="Ocultar por 30 minutos"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
